//@ts-check
import '../../styles/SummerrainUsersForm.css';
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../auth/authConfig';
import { Form, FormGroup, Label, Input, Button, Alert, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Spinner } from "react-bootstrap";
import env from 'react-dotenv';
import { FaPen, FaTrash, FaClone} from 'react-icons/fa';

function jwtDecode(token) {
    const [header, payload, signature] = token.split(".");
    const decodedPayload = JSON.parse(new TextDecoder().decode(Uint8Array.from(atob(payload), c => c.charCodeAt(0))));
    return decodedPayload;
}

export const SummerrainUsersForm = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchUpn, setSearchUpn] = useState(searchParams.get('upn') || "");
    const [userResults, setUserResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [message, setMessage] = useState(null);
    const initialLoad = useRef(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editUser, setEditUser] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    
    const [newUser, setNewUser] = useState({
        firstname: "",
        lastname: "",
        email: "",
        country: "US",
        companyName: "",
        language: "",
        unitnumber: "",
        currency: "",
        timezone: "",
        culture: "",
        rolename: ""
    });

    const { instance, accounts } = useMsal();

    useEffect(() => {
        if (initialLoad.current && searchUpn) {
            initialLoad.current = false;
            handleSearch({ preventDefault: () => {} });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const validateForm = (userData) => {
        const errors = {};
        if (!userData.firstname?.trim()) errors.firstname = "First name is required";
        if (!userData.lastname?.trim()) errors.lastname = "Last name is required";
        if (!userData.email?.trim()) errors.email = "Email is required";
        if (!userData.country?.trim()) errors.country = "Country is required";
        if (!userData.companyName?.trim()) errors.companyName = "Company is required";
        if (!userData.unitnumber?.trim()) errors.unitnumber = "Unit number is required";
        if (!userData.rolename?.trim()) errors.rolename = "Role name is required";
        return errors;
    };
    
    const handleCloneUser = (user) => {
        setNewUser({
            firstname: "",
            lastname: "",
            email: "",
            country: user.country,
            companyName: user.companyName,
            language: user.extension_7a38f9e88a754986a43690c623ec6302_language || "",
            unitnumber: user.extension_7a38f9e88a754986a43690c623ec6302_unitnumber || "",
            currency: user.extension_7a38f9e88a754986a43690c623ec6302_currency || "",
            timezone: user.extension_7a38f9e88a754986a43690c623ec6302_timezone || "",
            culture: user.extension_7a38f9e88a754986a43690c623ec6302_culture || "",
            rolename: user.extension_7a38f9e88a754986a43690c623ec6302_rolename || ""
        });
        setValidationErrors({});
        setShowCreateModal(true);
   };

    const handleSearch = async (event) => {
        setLoading(true);
        try {
            event.preventDefault();
            console.log(`Searching for: ${searchUpn}`);
            
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });
            const decodedAccessToken = jwtDecode(tokenResponse.accessToken);
            const encodedUpn = encodeURIComponent(searchUpn);

            const response = await fetch(`${env.API_SUMMERRAIN_USERS_ENDPOINT}/${encodedUpn}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                    "Content-Type": "application/json",
                }
            });

            console.log(`Response status: ${response.status}`);
            
            if (response.ok) {
                const data = await response.json();
                setUserResults(Array.isArray(data) ? data : [data]);
                setHasSearched(true);
                setMessage(null);
                setSearchParams({ upn: searchUpn });
            } else {
                const errorText = await response.text();
                console.log('Error response:', errorText);
                setUserResults([]);
                setMessage({ type: 'warning', text: `Status ${response.status}: ${errorText || 'No users found'}` });
            }
        } catch (error) {
            console.error('Search error:', error);
            setMessage({ type: 'danger', text: `Error: ${error.message}` });
        } finally {
            setLoading(false);
        }
    };

        const handleCreateUser = async () => {
        const errors = validateForm(newUser);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors({});
        setCreateLoading(true);
        setMessage(null); // Clear any previous messages
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const response = await fetch(`${env.API_SUMMERRAIN_USERS_ENDPOINT}`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(newUser),
            });

            if (response.ok) {
                const createdUser = await response.json(); 
                setShowCreateModal(false);
                setMessage({ type: 'success', text: 'User created successfully' });
                setUserResults([createdUser]); 
                setNewUser({
                    firstname: "",
                    lastname: "",
                    email: "",
                    country: "US",
                    companyName: "",
                    language: "",
                    unitnumber: "",
                    currency: "",
                    timezone: "",
                    culture: "",
                    rolename: ""
                });
            } else {
                const errorText = await response.text();
                console.error('Error response:', errorText);
                setMessage({ type: 'danger', text: `Error creating user: ${errorText}` });
                setShowCreateModal(false); // Close modal so error is visible
            }
        } catch (error) {
            setMessage({ type: 'danger', text: 'Error creating user' });
            setShowCreateModal(false); // Close modal so error is visible
        } finally {
            setCreateLoading(false);
        }
    };


    const handleInputChange = (field, value) => {
        setNewUser(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

        const handleEditUser = async () => {
        const errors = validateForm(editUser);
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors({});
        setEditLoading(true);
        setMessage(null);
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const response = await fetch(`${env.API_SUMMERRAIN_USERS_ENDPOINT}/${encodeURIComponent(editUser.upn)}`, {
                method: "PATCH",
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(editUser),
            });

            if (response.ok) {
                setShowEditModal(false);
                setMessage({ type: 'success', text: 'User updated successfully' });
                setUserResults(prev => prev.map(u =>
                    u.userPrincipalName === editUser.upn
                        ? {
                            ...u,
                            givenName: editUser.firstname,
                            surname: editUser.lastname,
                            mail: editUser.email,
                            country: editUser.country,
                            companyName: editUser.companyName,
                            extension_7a38f9e88a754986a43690c623ec6302_currency: editUser.currency,
                            extension_7a38f9e88a754986a43690c623ec6302_timezone: editUser.timezone,
                            extension_7a38f9e88a754986a43690c623ec6302_language: editUser.language,
                            extension_7a38f9e88a754986a43690c623ec6302_rolename: editUser.rolename,
                            extension_7a38f9e88a754986a43690c623ec6302_culture: editUser.culture,
                            extension_7a38f9e88a754986a43690c623ec6302_unitnumber: editUser.unitnumber,
                        }
                        : u
                ));
            } else {
                const errorText = await response.text();
                setShowEditModal(false); // Close modal so error is visible
                setMessage({ type: 'danger', text: `Error updating user: ${errorText}` });
            }
        } catch (error) {
            setShowEditModal(false); // Close modal so error is visible
            setMessage({ type: 'danger', text: 'Error updating user' });
        } finally {
            setEditLoading(false);
        }
    };


    const handleDeleteUser = async (userUpn) => {
        if (!window.confirm(`Are you sure you want to delete user ${userUpn}?`)) return;
        setDeleteLoading(userUpn);
        try {
            const tokenResponse = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            });

            const response = await fetch(`${env.API_SUMMERRAIN_USERS_ENDPOINT}/${encodeURIComponent(userUpn)}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${tokenResponse.accessToken}`,
                },
            });

            if (response.ok) {
                setMessage({ type: 'success', text: 'User deleted successfully' });
                setUserResults(prevResults=>prevResults.filter(u => u.userPrincipalName !== userUpn));
            } else {
                const errorText = await response.text();
                setMessage({ type: 'danger', text: `Error deleting user: ${errorText}` });
            }
        } catch (error) {
            setMessage({ type: 'danger', text: 'Error deleting user' });
        } finally {
            setDeleteLoading(null);
        }
    };

    const handleClear = () => {
        setSearchUpn("");
        setUserResults([]);
        setHasSearched(false);
        setMessage(null);
        setSearchParams({});
    };

    const handleEditInputChange = (field, value) => {
        setEditUser(prev => ({ ...prev, [field]: value }));
        if (validationErrors[field]) {
            setValidationErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    return (
        <div className="summerrain-container">
            <h3>Summerrain User Manager</h3>

            <Form onSubmit={handleSearch} className="summerrain-search-bar">
                <Label for="searchUpn" style={{ fontWeight: 500 }}>User Principal Name</Label>
                <div className="d-flex align-items-center gap-2">
                    <Input
                        type="email"
                        id="searchUpn"
                        value={searchUpn}
                        onChange={(e) => setSearchUpn(e.target.value)}
                        disabled={loading || hasSearched}
                        className="flex-grow-1"
                    />
                    {hasSearched ? (
                        <Button type="button" className="button" onClick={(e) => { e.preventDefault(); handleClear(); }} style={{ whiteSpace: 'nowrap' }}>
                            Clear
                        </Button>
                    ) : (
                        <Button disabled={loading} type="submit" className="button" style={{ whiteSpace: 'nowrap' }}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Search'}
                        </Button>
                    )}
                    <Button
                        type="button"
                        color="success"
                        onClick={() => { setShowCreateModal(true); setValidationErrors({}); }}
                        style={{ whiteSpace: 'nowrap' }}
                    >
                        Create User
                    </Button>
                </div>
            </Form>

            {message && (
                <div className="mt-3">
                    <Alert color={message.type}>{message.text}</Alert>
                </div>
            )}

            {userResults.map((user, index) => {
                const initials = `${(user.givenName || '?')[0]}${(user.surname || '?')[0]}`.toUpperCase();
                const val = (v) => v || '--';
                return (
                    <div key={index} className="summerrain-profile">
                        <div className="summerrain-profile-header">
                            <div className="summerrain-avatar">{initials}</div>
                            <div className="summerrain-profile-name">
                                <h4>{user.givenName} {user.surname}</h4>
                                <p>{user.userPrincipalName}</p>
                            </div>
                            <div className="summerrain-profile-actions">
                                <span title={user.hasDirectoryRoles ? "Cannot edit: user has directory roles" : "Edit"}>
                                    <Button
                                        color="link"
                                        size="sm"
                                        disabled={user.hasDirectoryRoles}
                                        onClick={() => {
                                            setEditUser({
                                                upn: user.userPrincipalName,
                                                firstname: user.givenName,
                                                lastname: user.surname,
                                                email: user.mail,
                                                mail: user.mail,
                                                country: user.country,
                                                companyName: user.companyName,
                                                currency: user.extension_7a38f9e88a754986a43690c623ec6302_currency || "",
                                                timezone: user.extension_7a38f9e88a754986a43690c623ec6302_timezone || "",
                                                language: user.extension_7a38f9e88a754986a43690c623ec6302_language || "",
                                                rolename: user.extension_7a38f9e88a754986a43690c623ec6302_rolename || "",
                                                culture: user.extension_7a38f9e88a754986a43690c623ec6302_culture || "",
                                                unitnumber: user.extension_7a38f9e88a754986a43690c623ec6302_unitnumber || ""
                                            });
                                            setValidationErrors({});
                                            setShowEditModal(true);
                                        }}
                                    >
                                        <FaPen className="summerrain-icon" />
                                    </Button>
                                </span>
                                <Button
                                    color="link"
                                    size="sm"
                                    title="Clone"
                                    onClick={(e) => { e.stopPropagation(); handleCloneUser(user); }}
                                >
                                    <FaClone className="summerrain-icon" />
                                </Button>
                                <span title={user.hasDirectoryRoles ? "Cannot delete: user has directory roles" : "Delete"}>
                                    <Button
                                        color="link"
                                        size="sm"
                                        disabled={user.hasDirectoryRoles || deleteLoading === user.userPrincipalName}
                                        onClick={() => handleDeleteUser(user.userPrincipalName)}
                                    >
                                        {deleteLoading === user.userPrincipalName ? <Spinner size="sm" /> : <FaTrash className="summerrain-icon" />}
                                    </Button>
                                </span>
                            </div>
                        </div>
                        <div className="summerrain-profile-body">
                            <div className="summerrain-profile-columns">
                                <div className="summerrain-profile-section">
                                    <h6 className="summerrain-section-label">User</h6>
                                    <div className="summerrain-detail-item">
                                        <label>First Name</label>
                                        <span>{val(user.givenName)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Last Name</label>
                                        <span>{val(user.surname)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Email</label>
                                        <span>{val(user.mail)}</span>
                                    </div>
                                </div>
                                <div className="summerrain-profile-section">
                                    <h6 className="summerrain-section-label">Organization</h6>
                                    <div className="summerrain-detail-item">
                                        <label>Company</label>
                                        <span>{val(user.companyName)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Role</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_rolename)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Unit Number</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_unitnumber)}</span>
                                    </div>
                                </div>
                                <div className="summerrain-profile-section">
                                    <h6 className="summerrain-section-label">Locale</h6>
                                    <div className="summerrain-detail-item">
                                        <label>Country</label>
                                        <span>{val(user.country)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Language</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_language)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Currency</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_currency)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Time Zone</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_timezone)}</span>
                                    </div>
                                    <div className="summerrain-detail-item">
                                        <label>Culture</label>
                                        <span>{val(user.extension_7a38f9e88a754986a43690c623ec6302_culture)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}

                <Modal isOpen={showCreateModal} toggle={() => setShowCreateModal(false)} size="lg">
                    <ModalHeader toggle={() => setShowCreateModal(false)}>Create New User</ModalHeader>
                    <ModalBody>
                        <h6 className="summerrain-modal-section-label">User</h6>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>First Name <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.firstname} onChange={(e) => handleInputChange('firstname', e.target.value)} invalid={!!validationErrors.firstname} />
                                    {validationErrors.firstname && <div className="invalid-feedback d-block">{validationErrors.firstname}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Last Name <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.lastname} onChange={(e) => handleInputChange('lastname', e.target.value)} invalid={!!validationErrors.lastname} />
                                    {validationErrors.lastname && <div className="invalid-feedback d-block">{validationErrors.lastname}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Email <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.email} onChange={(e) => handleInputChange('email', e.target.value)} invalid={!!validationErrors.email} />
                                    {validationErrors.email && <div className="invalid-feedback d-block">{validationErrors.email}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <h6 className="summerrain-modal-section-label">Organization</h6>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Company <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.companyName} onChange={(e) => handleInputChange('companyName', e.target.value)} invalid={!!validationErrors.companyName} />
                                    {validationErrors.companyName && <div className="invalid-feedback d-block">{validationErrors.companyName}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Role Name <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.rolename} onChange={(e) => handleInputChange('rolename', e.target.value)} invalid={!!validationErrors.rolename} />
                                    {validationErrors.rolename && <div className="invalid-feedback d-block">{validationErrors.rolename}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Unit Number <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.unitnumber} onChange={(e) => handleInputChange('unitnumber', e.target.value)} invalid={!!validationErrors.unitnumber} />
                                    {validationErrors.unitnumber && <div className="invalid-feedback d-block">{validationErrors.unitnumber}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <h6 className="summerrain-modal-section-label">Locale</h6>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Country <span style={{color: 'red'}}>*</span></Label>
                                    <Input value={newUser.country} onChange={(e) => handleInputChange('country', e.target.value)} invalid={!!validationErrors.country} />
                                    {validationErrors.country && <div className="invalid-feedback d-block">{validationErrors.country}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Language</Label>
                                    <Input value={newUser.language} onChange={(e) => handleInputChange('language', e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Currency</Label>
                                    <Input value={newUser.currency} onChange={(e) => handleInputChange('currency', e.target.value)} />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Time Zone</Label>
                                    <Input value={newUser.timezone} onChange={(e) => handleInputChange('timezone', e.target.value)} />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label>Culture</Label>
                                    <Input value={newUser.culture} onChange={(e) => handleInputChange('culture', e.target.value)} />
                                </FormGroup>
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                        <Button color="primary" onClick={handleCreateUser} disabled={createLoading}>
                            {createLoading ? <Spinner size="sm" /> : 'Create User'}
                        </Button>
                    </ModalFooter>
                </Modal>

                <Modal isOpen={showEditModal} toggle={() => setShowEditModal(false)} size="lg">
                    <ModalHeader toggle={() => setShowEditModal(false)}>Edit User</ModalHeader>
                    <ModalBody>
                        {editUser && (
                            <>
                                <h6 className="summerrain-modal-section-label">User</h6>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>First Name <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.firstname} onChange={(e) => handleEditInputChange('firstname', e.target.value)} invalid={!!validationErrors.firstname} />
                                            {validationErrors.firstname && <div className="invalid-feedback d-block">{validationErrors.firstname}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Last Name <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.lastname} onChange={(e) => handleEditInputChange('lastname', e.target.value)} invalid={!!validationErrors.lastname} />
                                            {validationErrors.lastname && <div className="invalid-feedback d-block">{validationErrors.lastname}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Email <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.email} onChange={(e) => handleEditInputChange('email', e.target.value)} invalid={!!validationErrors.email} />
                                            {validationErrors.email && <div className="invalid-feedback d-block">{validationErrors.email}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <h6 className="summerrain-modal-section-label">Organization</h6>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Company <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.companyName} onChange={(e) => handleEditInputChange('companyName', e.target.value)} invalid={!!validationErrors.companyName} />
                                            {validationErrors.companyName && <div className="invalid-feedback d-block">{validationErrors.companyName}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Role Name <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.rolename} onChange={(e) => handleEditInputChange('rolename', e.target.value)} invalid={!!validationErrors.rolename} />
                                            {validationErrors.rolename && <div className="invalid-feedback d-block">{validationErrors.rolename}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Unit Number <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.unitnumber} onChange={(e) => handleEditInputChange('unitnumber', e.target.value)} invalid={!!validationErrors.unitnumber} />
                                            {validationErrors.unitnumber && <div className="invalid-feedback d-block">{validationErrors.unitnumber}</div>}
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <h6 className="summerrain-modal-section-label">Locale</h6>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Country <span style={{color: 'red'}}>*</span></Label>
                                            <Input value={editUser.country} onChange={(e) => handleEditInputChange('country', e.target.value)} invalid={!!validationErrors.country} />
                                            {validationErrors.country && <div className="invalid-feedback d-block">{validationErrors.country}</div>}
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Language</Label>
                                            <Input value={editUser.language} onChange={(e) => handleEditInputChange('language', e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Currency</Label>
                                            <Input value={editUser.currency} onChange={(e) => handleEditInputChange('currency', e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Time Zone</Label>
                                            <Input value={editUser.timezone} onChange={(e) => handleEditInputChange('timezone', e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                    <Col md={4}>
                                        <FormGroup>
                                            <Label>Culture</Label>
                                            <Input value={editUser.culture} onChange={(e) => handleEditInputChange('culture', e.target.value)} />
                                        </FormGroup>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setShowEditModal(false)}>Cancel</Button>
                        <Button color="primary" onClick={handleEditUser} disabled={editLoading}>
                            {editLoading ? <Spinner size="sm" /> : 'Update User'}
                        </Button>
                    </ModalFooter>
                </Modal>
        </div>
    );
};
